import { useId, useState, type ReactNode } from 'react';
import {
  Button,
  EmptyState,
  Panel,
  PanelHeader,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
} from '@/components/ui';
import { formatMoneyText } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';
import { CompactFigure } from '@/components/dashboards/TargetTiles';
import {
  MetricDrilldownModal,
  type MetricDrilldownSource,
} from '@/components/dashboards/drilldown/MetricDrilldownModal';
import { NewBusinessBreakdownModal } from '@/components/commissions/NewBusinessAccounts';
import { PiCardField, PiCardList } from '@/components/product-insights/pi-kit';
import {
  EARNED_HEADING,
  NEW_BUSINESS_LABELS,
  gateLabel,
  hasEarned,
  hasNewBusiness,
  isRemainder,
  personalTargetLabel,
  progressBar,
} from '@/lib/commission-progress';
import { compactMoneyParts } from '@/lib/targets';
import type { CommissionOverviewRowsFigure, CommissionProgress } from '@/lib/types';
import { useCommissionOverview } from '@/lib/use-commission-overview';

/**
 * TARGETS & COMMISSION — the Financial Analytics section (owner 2026-10-27).
 *
 * One row per person with a commission assignment in the selected cycle, then
 * one per remainder-target ("derived") user, in the server's order. Every row
 * is in its OWN currency (the scheme's, or the remainder base currency), so
 * nothing here is ever summed down a column: there is no total row.
 *
 * - Management / Super Admin only (`commissions.read` scope `all`). A 403 and
 *   an idle period (no cycle, custom range) render NOTHING, not a refusal.
 * - Remainder rows carry only the personal half: the derived target with its
 *   label, the invoiced actual and the %; regional, commission and gate cells
 *   are `—`. They never earn commission.
 * - Percentages come from `progressBar` (BigInt on the decimal strings). A
 *   null target is "no commitment", so its % is `—`, never 0.
 * - Every money cell is compact K/M (owner 2026-10-27, "use the full K & M
 *   concept"): `compactMoneyParts` + `CompactFigure`, the exact figure on the
 *   `title` and as the screen-reader text. See `CompactAmount`.
 * - Owner 2026-10-28: the Invoiced and Regional invoiced figures are buttons
 *   that open the ONE drill-down modal mounted here (`MetricDrilldownModal`,
 *   source `commission` -> `GET /commissions/overview/rows`). Targets, %s,
 *   commission earned and gate stay plain; a `—` figure is not clickable.
 * - Owner 2026-10-28: four new business columns after Regional % (accounts
 *   this period, same accounts last period, new business, new business
 *   commission). Each figure opens the ONE `NewBusinessBreakdownModal` mounted
 *   here (`GET /commissions/progress/new-business`), a second drill state beside
 *   the documents one. Remainder rows show the comparison figures and `—` for
 *   commission. No `newBusiness` key (older API) or `null` = every cell `—`.
 */
export function TargetsCommissionPanel({ periodId }: { periodId: number | null }) {
  const headingId = useId();
  const overview = useCommissionOverview(periodId);
  const [drill, setDrill] = useState<DrillTarget | null>(null);
  const [newBusinessDrill, setNewBusinessDrill] = useState<NewBusinessDrillTarget | null>(null);

  if (overview.status === 'idle' || overview.status === 'forbidden') return null;

  const periodName = overview.data?.period.name ?? null;
  const drillSource: MetricDrilldownSource | null =
    drill === null || periodId === null
      ? null
      : { kind: 'commission', params: { periodId, userId: drill.userId, figure: drill.figure } };
  const drillTitle =
    drill === null
      ? ''
      : [FIGURE_LABEL[drill.figure], drill.personName, periodName].filter((part) => part !== null).join(' · ');
  const newBusinessParams =
    newBusinessDrill === null || periodId === null ? null : { userId: newBusinessDrill.userId, periodId };
  const newBusinessTitle =
    newBusinessDrill === null
      ? ''
      : [NEW_BUSINESS_LABELS.newBusiness, newBusinessDrill.personName, periodName]
          .filter((part) => part !== null)
          .join(' · ');
  const opens: Openers = { drill: setDrill, newBusiness: setNewBusinessDrill };

  return (
    <Panel className="overflow-hidden" aria-labelledby={headingId}>
      <PanelHeader id={headingId} title="Targets & commission" />
      {overview.status === 'loading' ? (
        <LoadingRows />
      ) : overview.status === 'error' ? (
        <div className="p-4">
          <Notice
            tone="danger"
            title="Couldn’t read commission targets."
            action={
              <Button size="sm" variant="secondary" onClick={overview.reload}>
                Retry
              </Button>
            }
          >
            {overview.error?.message ?? 'The request failed.'}
          </Notice>
        </div>
      ) : overview.data === null || overview.data.rows.length === 0 ? (
        <EmptyState title="No commission assignments or targets in this cycle." />
      ) : (
        <OverviewRows rows={overview.data.rows} onOpen={opens} />
      )}

      <MetricDrilldownModal
        open={drillSource !== null}
        onClose={() => setDrill(null)}
        source={drillSource}
        title={drillTitle}
      />
      <NewBusinessBreakdownModal
        open={newBusinessParams !== null}
        onClose={() => setNewBusinessDrill(null)}
        params={newBusinessParams}
        title={newBusinessTitle}
      />
    </Panel>
  );
}

/* -------------------------------------------------------------------------
 * One row's cells, decided once so the table and the cards cannot disagree.
 * --------------------------------------------------------------------- */

const COLUMNS = [
  'Person',
  'Currency',
  'Individual target',
  'Invoiced',
  '% of target',
  'Regional target',
  'Regional invoiced',
  'Regional %',
  NEW_BUSINESS_LABELS.accountsThisPeriod,
  NEW_BUSINESS_LABELS.accountsLastPeriod,
  NEW_BUSINESS_LABELS.newBusiness,
  NEW_BUSINESS_LABELS.newBusinessCommission,
  EARNED_HEADING,
  'Gate',
] as const;

type ColumnLabel = (typeof COLUMNS)[number];

function Dash() {
  return <span className="text-fg-subtle">—</span>;
}

/**
 * A money figure, compact (`885.74K`, `2.30M`; below 1,000 the exact value).
 * The exact decimal string, grouped as text, is the `title` and the only text a
 * screen reader hears; the compact form is `aria-hidden`. `null` (or blank) is
 * `—`, never 0.
 */
function CompactAmount({ amount, currency }: { amount: string | null; currency: string }) {
  if (amount === null || amount.trim() === '') return <Dash />;
  const exact = formatMoneyText(amount, currency);
  return (
    <span className="tabular-nums whitespace-nowrap" title={exact}>
      <span aria-hidden="true">
        <CompactFigure parts={compactMoneyParts(amount, currency)} />
      </span>
      <span className="sr-only">{exact}</span>
    </span>
  );
}

/** Which figure of a row the drill-down lists (`GET /commissions/overview/rows`). */
type DrillFigure = CommissionOverviewRowsFigure;

/** What the one modal is showing, or `null` when it is closed. */
interface DrillTarget {
  userId: number;
  figure: DrillFigure;
  personName: string;
}

type OpenDrill = (target: DrillTarget) => void;

/** Whose new business breakdown the second modal is showing, or `null`. */
interface NewBusinessDrillTarget {
  userId: number;
  personName: string;
}

/** The two drill-downs a row's figures can open. */
interface Openers {
  drill: OpenDrill;
  newBusiness: (target: NewBusinessDrillTarget) => void;
}

const FIGURE_LABEL: Record<DrillFigure, string> = {
  personal: 'Invoiced',
  regional: 'Regional invoiced',
};

/**
 * An invoiced figure the reader can click to list the documents behind it
 * (owner 2026-10-28). Same affordance as the target tiles' `TileFigures`
 * button: borderless, underline on hover and focus. The aria-label replaces
 * the content for a screen reader, so the exact figure rides in it. A `null`
 * figure is a plain `—`, never a button.
 */
function DrillAmount({
  amount,
  currency,
  label,
  onOpen,
}: {
  amount: string | null;
  currency: string;
  label: string;
  onOpen: () => void;
}) {
  if (amount === null || amount.trim() === '') return <Dash />;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${label}: ${formatMoneyText(amount, currency)}`}
      className="cursor-pointer rounded-sm border-0 bg-transparent p-0 underline-offset-4 decoration-hairline-strong hover:underline focus-visible:underline"
    >
      <CompactAmount amount={amount} currency={currency} />
    </button>
  );
}

function pctCell(pct: number | null): ReactNode {
  return pct === null ? <Dash /> : <span className="tabular-nums">{pct.toLocaleString()}%</span>;
}

function rowKey(row: CommissionProgress): string {
  return `${row.kind ?? 'assignment'}-${row.userId}`;
}

function personName(row: CommissionProgress): string {
  return row.user?.name ?? `User #${row.userId}`;
}

/** Every cell of one row, keyed by its column heading. */
function rowCells(row: CommissionProgress, onOpen: Openers): Record<ColumnLabel, ReactNode> {
  const remainder = isRemainder(row);
  const name = personName(row);
  const drill = (figure: DrillFigure) => (amount: string | null) => (
    <DrillAmount
      amount={amount}
      currency={row.currency}
      label={`Show the documents behind ${name}’s ${FIGURE_LABEL[figure].toLowerCase()}`}
      onOpen={() => onOpen.drill({ userId: row.userId, figure, personName: name })}
    />
  );
  const newBusinessDrill = (label: string) => (amount: string | null | undefined) => (
    <DrillAmount
      amount={amount ?? null}
      currency={row.currency}
      label={`Show the accounts behind ${name}’s ${label.toLowerCase()}`}
      onOpen={() => onOpen.newBusiness({ userId: row.userId, personName: name })}
    />
  );
  const newBusiness = hasNewBusiness(row) ? row.newBusiness : null;
  const personal = progressBar(row.personal.actual, row.personal.target);
  // Remainder users (Paras) carry a combined regional figure too (owner 2026-10-27).
  const regional = row.regional ? progressBar(row.regional.actual, row.regional.target) : null;
  const earned = !remainder && hasEarned(row) ? row.earned : null;

  return {
    Person: <span className="font-medium text-fg">{name}</span>,
    Currency: <span className="tabular-nums">{row.currency}</span>,
    'Individual target': (
      <>
        <CompactAmount amount={row.personal.target} currency={row.currency} />
        {remainder && (
          <span className="mt-0.5 block text-2xs text-fg-subtle">{personalTargetLabel(row)}</span>
        )}
      </>
    ),
    Invoiced: drill('personal')(row.personal.actual),
    '% of target': pctCell(personal.pct),
    'Regional target':
      regional === null ? <Dash /> : <CompactAmount amount={regional.target} currency={row.currency} />,
    'Regional invoiced':
      regional === null ? <Dash /> : drill('regional')(regional.actual),
    'Regional %': regional === null ? <Dash /> : pctCell(regional.pct),
    [NEW_BUSINESS_LABELS.accountsThisPeriod]:
      newBusiness === null ? (
        <Dash />
      ) : (
        newBusinessDrill(NEW_BUSINESS_LABELS.accountsThisPeriod)(newBusiness.accountsThisPeriod.actual)
      ),
    [NEW_BUSINESS_LABELS.accountsLastPeriod]:
      newBusiness === null ? (
        <Dash />
      ) : (
        newBusinessDrill(NEW_BUSINESS_LABELS.accountsLastPeriod)(newBusiness.accountsLastPeriod.actual)
      ),
    [NEW_BUSINESS_LABELS.newBusiness]:
      newBusiness === null ? <Dash /> : newBusinessDrill(NEW_BUSINESS_LABELS.newBusiness)(newBusiness.newBusiness),
    // Remainder rows never earn commission; `undefined` = an older API.
    [NEW_BUSINESS_LABELS.newBusinessCommission]:
      newBusiness === null || earned === null ? (
        <Dash />
      ) : (
        newBusinessDrill(NEW_BUSINESS_LABELS.newBusinessCommission)(earned.newBusinessCommission)
      ),
    [EARNED_HEADING]:
      earned === null ? <Dash /> : <CompactAmount amount={earned.payout} currency={row.currency} />,
    Gate:
      earned === null ? (
        <Dash />
      ) : (
        <span className={earned.gateMet ? 'text-success' : 'text-fg-muted'}>{gateLabel(earned.gateMet)}</span>
      ),
  };
}

/** Every column but Person and Gate holds a figure. */
function isNumericColumn(column: ColumnLabel): boolean {
  return column !== 'Person' && column !== 'Gate';
}

function OverviewRows({ rows, onOpen }: { rows: readonly CommissionProgress[]; onOpen: Openers }) {
  return (
    <>
      <div className="hidden md:block">
        <TableWrap>
          <Table>
            <caption className="sr-only">
              Each person’s invoiced revenue against their commission targets, in their own currency.
            </caption>
            <OverviewHead />
            <TBody>
              {rows.map((row) => {
                const cells = rowCells(row, onOpen);
                return (
                  <TR key={rowKey(row)}>
                    {COLUMNS.map((column) => (
                      <TD
                        key={column}
                        verticalAlign="top"
                        numeric={isNumericColumn(column)}
                        className="whitespace-nowrap"
                        {...(column === 'Person' ? { frozen: 'left' as const, frozenOffset: 0 } : {})}
                      >
                        {cells[column]}
                      </TD>
                    ))}
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </TableWrap>
      </div>

      <div className="p-3 md:hidden">
        <PiCardList
          items={rows}
          getKey={rowKey}
          render={(row) => {
            const cells = rowCells(row, onOpen);
            return (
              <div className="grid min-w-0 gap-1">
                <p className="text-sm">{cells.Person}</p>
                {COLUMNS.filter((column) => column !== 'Person').map((column) => (
                  <PiCardField key={column} label={column} value={cells[column]} />
                ))}
              </div>
            );
          }}
        />
      </div>
    </>
  );
}

function OverviewHead() {
  return (
    <THead>
      <TR>
        {COLUMNS.map((column) => (
          <TH
            key={column}
            numeric={isNumericColumn(column)}
            className="whitespace-nowrap"
            {...(column === 'Person' ? { frozen: 'left' as const, frozenOffset: 0 } : {})}
          >
            {column}
          </TH>
        ))}
      </TR>
    </THead>
  );
}

function LoadingRows() {
  return (
    <>
      <div className="hidden md:block">
        <TableWrap>
          <Table>
            <caption className="sr-only">Loading commission targets.</caption>
            <OverviewHead />
            <TBody>
              {Array.from({ length: 3 }, (_, i) => (
                <TR key={i}>
                  {COLUMNS.map((column) => (
                    <TD
                      key={column}
                      {...(column === 'Person' ? { frozen: 'left' as const, frozenOffset: 0 } : {})}
                    >
                      <Skeleton className={column === 'Person' ? 'h-3 w-32' : 'ml-auto h-3 w-16'} />
                    </TD>
                  ))}
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      </div>
      <div className="grid gap-2 p-3 md:hidden">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    </>
  );
}
